// src/functions/getAlertContextData.js
import store from '../redux/store';
import api from '../functions/api';

const normalize = (t) =>
  typeof t === 'string' ? t.trim().toUpperCase() : t;

const toSectorKey = (s) =>
  typeof s === 'string' ? s.trim().replace(/\s+/g, '_') : s;

const ctxCache = new Map(); // key: ticker -> result object

function mapRow(columns, row) {
  return columns.reduce((acc, col, i) => {
    acc[col] = row[i];
    return acc;
  }, {});
}

function parseInstitutionalKey(k) {
  // "Institutional_trend_Global_Technology" -> {region:"Global", sector:"Technology"}
  const parts = k.split('Institutional_trend_')[1]?.split('_') || [];
  const region = parts.slice(0, -1).join('_') || null; // handles "Europe_Basic_Materials"
  const sector = parts.slice(-1)[0] || null;
  return { region, sector };
}

function buildInstitutionalViews(kv, sector) {
  const sectorKey = toSectorKey(sector);
  const pickedEntries = Object.entries(kv).filter(([k]) =>
    /^Institutional_trend_/i.test(k) &&
    new RegExp(`_${sectorKey}$`, 'i').test(k)
  );

  // Array form to make logging/rendering trivial
  const array = pickedEntries.map(([key, value]) => {
    const { region } = parseInstitutionalKey(key);
    return { key, region, value };
  });

  // Compact text form
  const text = array.map(({ region, value }) => `${region}: ${value}`).join(' | ');

  // Map form (original)
  const map = pickedEntries.reduce((acc, [k, v]) => { acc[k] = v; return acc; }, {});

  return {
    map: Object.keys(map).length ? map : null,
    array: array.length ? array : null,
    text: array.length ? text : null,
  };
}

/**
 * Fetch alert context in one place:
 * - sector & performance via API (/api/stock_alert_context)
 * - sector trend from Redux (portfolio.data.Sector_trend)
 * - institutional sector trends from Redux (portfolio.data.Vector_institutional_trend)
 */
export default async function getAlertContextData(ticker, seedSector = null) {
  const t = normalize(ticker);
  if (!t) return null;

  if (ctxCache.has(t)) return ctxCache.get(t);

  let sector = seedSector || null;
  let performance = null;

  try {
    const { data } = await api.get('/stock_alert_context', {
      params: { symbol: t },
    });
    sector = data?.sector ?? sector ?? null;
    performance = data?.performance ?? null;
  } catch {
    // ignore
  }

  const state = store.getState();

  // Sector Trend
  const st = state?.portfolio?.data?.Sector_trend;
  let sectorTrendRow = null;
  let sectorTrendMapped = null;

  if (st?.rows && st?.columns && sector) {
    const row = st.rows.find(
      (r) => String(r[2]).toLowerCase() === String(sector).toLowerCase()
    );
    if (row) {
      sectorTrendRow = row;
      sectorTrendMapped = mapRow(st.columns, row);
    }
  }

  // Institutional sector trends
  const vit = state?.portfolio?.data?.Vector_institutional_trend;
  let institutionalSectorTrends = null;
  let institutionalSectorTrendsArray = null;
  let institutionalSectorTrendsText = null;

  if (vit?.rows?.length && vit?.columns?.length && sector) {
    const latest = mapRow(vit.columns, vit.rows[0]);
    const views = buildInstitutionalViews(latest, sector);
    institutionalSectorTrends = views.map;
    institutionalSectorTrendsArray = views.array;
    institutionalSectorTrendsText = views.text;
  }

  const result = {
    ticker: t,
    sector,
    performance,
    sectorTrendRow,
    sectorTrendMapped,
    institutionalSectorTrends,        // map form
    institutionalSectorTrendsArray,   // array form  [{key, region, value}, ...]
    institutionalSectorTrendsText,    // text form   "Global: Weak Buy | USA: Neutral | ..."
  };

  ctxCache.set(t, result);

  // Log only simple, readable fields
  // console.log(
  //   `getAlertContextData(${t}) -> sector: ${sector}, ` +
  //   `hasPerformance: ${performance ? 'yes' : 'no'}, ` +
  //   `sectorTrendFound: ${sectorTrendRow ? 'yes' : 'no'}, ` +
  //   `institutionalSectorTrends: ${institutionalSectorTrendsText || 'none'}`
  // );

  // console.log(result)
  return result;
}
